import { FC, useMemo } from 'react';
import { observer } from 'mobx-react';

import { Partner } from '@constants/paymentLink';
import useStore from '@hooks/useStore';

import { PaymentFormProps } from '../types';
import GenericSpeiConfirmation from './GenericSpeiConfirmation';
import OysterSpeiInfo from './OysterSpeiInfo';

export const getPartner = (partner: Partner) => {
  switch (partner) {
    case Partner.STP:
      return GenericSpeiConfirmation;
    case Partner.ARCUS:
      return GenericSpeiConfirmation;
    case Partner.FINTOC:
      return GenericSpeiConfirmation;
    case Partner.MONATO:
      return GenericSpeiConfirmation;
    case Partner.OYSTER:
    default:
      return OysterSpeiInfo;
  }
};

const SpeiPaymentForm: FC<PaymentFormProps> = ({ onValidationError }) => {
  const {
    dlocal,
    arcus,
    fintoc,
    monato,
    checkout: { isAmountAllowedToCardPayment, isAllowedToPayWithSpei },
    links: {
      paymentConfig: {
        paymentMethods: {
          spei: { partner },
        },
      },
      paymentLink: { paymentLinkId },
    },
  } = useStore();
  const PartnerForm = getPartner(partner as Partner);

  const isLoadingSpeiReference = useMemo(() => {
    switch (partner) {
      case Partner.STP:
        return dlocal.flags.isLoadingSpeiReference;
      case Partner.ARCUS:
        return arcus.flags.isLoadingSpeiReference;
      case Partner.FINTOC:
        return fintoc.flags.isLoadingSpeiReference;
      case Partner.MONATO:
        return monato.flags.isLoadingSpeiReference;
      case Partner.OYSTER:
      default:
        return false;
    }
  }, [
    partner,
    dlocal.flags.isLoadingSpeiReference,
    arcus.flags.isLoadingSpeiReference,
    fintoc.flags.isLoadingSpeiReference,
    monato.flags.isLoadingSpeiReference,
  ]);

  const partnerFormProps = useMemo(() => {
    const commonProps = {
      isAmountAllowedToCardPayment,
      isAllowedToPayWithSpei,
      paymentLinkId,
      partner,
    };
    switch (partner) {
      case Partner.STP:
        return {
          isLoadingSpeiReference: isLoadingSpeiReference,
          getSpeiReference: dlocal.getSpeiReference,
          ...commonProps,
        };
      case Partner.ARCUS:
        return {
          isLoadingSpeiReference: isLoadingSpeiReference,
          getSpeiReference: arcus.getSpeiReference,
          ...commonProps,
        };
      case Partner.FINTOC:
        return {
          isLoadingSpeiReference: isLoadingSpeiReference,
          getSpeiReference: fintoc.getSpeiReference,
          ...commonProps,
        };
      case Partner.MONATO:
        return {
          isLoadingSpeiReference: isLoadingSpeiReference,
          getSpeiReference: monato.getSpeiReference,
          ...commonProps,
        };
      case Partner.OYSTER:
      default:
        return {
          isLoadingSpeiReference: isLoadingSpeiReference,
          getSpeiReference: () => null,
          ...commonProps,
        };
    }
  }, [partner, isLoadingSpeiReference]);

  return (
    <div role="tab-panel" className="tab-content spei-content" data-testid="spei-info-panel">
      {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
      {/* @ts-ignore */}
      <PartnerForm onValidationError={onValidationError} {...partnerFormProps} />
    </div>
  );
};

export default observer(SpeiPaymentForm);
